import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { isIOSApp } from "@/lib/platform";
import WebsiteOnlyNotice from "@/components/home/WebsiteOnlyNotice";

export default function Contact() {
  const [newsletter, setNewsletter] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const [joining, setJoining] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      const { data } = await api.post("/payments/subscribe", { email: newsletter, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setJoining(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/messages", form);
      toast.success("Message received. Respect!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 lg:py-32 px-4 sm:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-4">Stay Connected</p>
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Join the movement
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-blood p-8 sm:p-10 clip-frame flex flex-col justify-between"
            data-testid="newsletter-card"
          >
            <div>
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-display font-black uppercase text-2xl sm:text-3xl">Zo Pound Insider</h3>
                <span data-testid="membership-price" className="shrink-0 bg-black text-white font-display font-black text-lg px-3 py-1">
                  $1.99<span className="font-mono text-[10px] text-gray-300 ml-1">/ MO</span>
                </span>
              </div>
              <p className="mt-4 text-red-100 leading-relaxed">
                Full access to the app — every episode, every drop, and the live chat with the movement. New episodes and untold stories straight to your inbox. Cancel anytime.
              </p>
            </div>
            {isIOSApp() ? (
              <WebsiteOnlyNotice what="Memberships" path="/#contact" testId="ios-subscribe-notice" />
            ) : (
<form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={newsletter}
                onChange={(e) => setNewsletter(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-black/30 border border-white/30 px-4 py-3 text-white placeholder-red-200/60 focus:outline-none focus:border-white font-mono text-sm"
              />
              <button
                data-testid="newsletter-subscribe-button"
                type="submit"
                disabled={joining}
                className="bg-black text-white font-display font-bold uppercase tracking-wider px-6 py-3 hover:bg-ink/80 disabled:opacity-60 transition-colors"
              >
                {joining ? "Redirecting…" : "Subscribe — $1.99/mo"}
              </button>
            </form>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-elevate border border-white/10 p-8 sm:p-10 clip-frame"
            data-testid="contact-form-card"
          >
            <h3 className="font-display font-black uppercase text-2xl sm:text-3xl">Reach the Show</h3>
            <a
              data-testid="contact-email-link"
              href="mailto:simplicityfilmsllc@gmail.com"
              className="mt-3 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-blood transition-colors"
            >
              <Mail size={14} /> simplicityfilmsllc@gmail.com
            </a>
            <form onSubmit={sendMessage} className="mt-6 grid gap-4">
              <input
                data-testid="contact-name-input"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="bg-ink border border-white/15 px-4 py-3 focus:outline-none focus:border-blood text-sm"
              />
              <input
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Your email"
                className="bg-ink border border-white/15 px-4 py-3 focus:outline-none focus:border-blood text-sm"
              />
              <textarea
                data-testid="contact-message-input"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Your message"
                className="bg-ink border border-white/15 px-4 py-3 focus:outline-none focus:border-blood text-sm resize-none"
              />
              <button
                data-testid="contact-send-button"
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors"
              >
                <Send size={16} /> {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
